import type React from "react";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useViewportFrame } from "../hooks/useViewportFrame";
import { Link, useNavigate, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import {
  CurationCheckCard,
  CurationVerdictPanel,
  cadenceError,
  cadencePayload,
  verifiedCell,
  verifiedLine,
  VERIFY_HELP,
  type CurationCheck,
  type VerificationFields,
  supersessionPillClass,
} from "../lib/curationCheck.js";
import { proposalOriginLabel, proposedByLabel } from "../lib/proposalOrigin.js";
import { PageLayout } from "../components/PageLayout.js";
import { TrailLinks, useWalkedTrail } from "../components/ActivityTrail.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { useConfirm } from "../components/ConfirmDialog.js";
import { TenantKnowledgeProposedTab } from "./TenantKnowledgeProposedTab.js";
import { USAGE_HELP, usageCountCell, usageProse, usageTimeCell, usageTimeSortValue, type EntryUsage } from "../lib/knowledgeUsage.js";

// ─────────────────────────────────────────────
// Types (mirror hub serializeKnowledgeEntry)
// ─────────────────────────────────────────────

export type KnowledgeEntry = {
  name: string;
  type: string;
  description: string;
  scope_tags: string[];
  status: string; // accepted | draft | archived (archived = location)
  related: string[];
  body: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  archive_reason?: string;
  archived_at?: string;
  /** Door retrieval counters; null = usage unavailable (renders as "—"). */
  usage?: EntryUsage | null;
  /** ADR-0201 — when a curator last confirmed this entry, and whether that lapsed. */
  verified_at?: string | null;
  verified_by_actor?: string | null;
  verify_every_days?: number | null;
  lapsed?: boolean;
};

const KNOWLEDGE_TYPES = [
  "join_path", "column_derivation", "code_definition", "filter_idiom",
  "canonical_query", "metric_definition", "synonym", "temporal_rule", "anti_pattern",
];

const STATUSES = ["draft", "accepted", "archived"];
const SLUG_RE = /^[a-z0-9-]{3,64}$/;

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

function fmtDateTime(s: string) {
  return new Date(s).toLocaleString();
}

type KnowledgeSortKey = "updated" | "search_hits" | "reads" | "last_hit" | "last_read" | "verified";
type KnowledgeSortDir = "asc" | "desc";

function KnowledgeSortableTH(props: {
  col: KnowledgeSortKey;
  sortKey: KnowledgeSortKey | null;
  sortDir: KnowledgeSortDir;
  onClick: () => void;
  title?: string;
  children: React.ReactNode;
}) {
  const active = props.sortKey === props.col;
  const arrow = active ? (props.sortDir === "asc" ? " ↑" : " ↓") : "";
  return (
    <th style={{ cursor: "pointer", userSelect: "none" }} onClick={props.onClick} title={props.title}>
      {props.children}
      {arrow}
    </th>
  );
}


function statusPill(status: string) {
  const cls = status === "accepted" ? "pillOk" : status === "draft" ? "pillWarn" : "";
  return <span className={`pill ${cls}`}>{status}</span>;
}

function typePill(t: string) {
  return <span className="pill">{t.replace(/_/g, " ")}</span>;
}

/**
 * Markdown body renderer — full typographic treatment (headings, fenced SQL,
 * tables). remark-gfm is required: pipe tables are a GFM extension and render
 * as unreadable raw text without it.
 */
export function MarkdownView({ body }: { body: string }) {
  return (
    <div className="knowledgeMarkdown">
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  );
}

// ─────────────────────────────────────────────
// Main page: tabs + list
// ─────────────────────────────────────────────

type Tab = "knowledge" | "guidance" | "proposed";

export function TenantAdminTenantKnowledge() {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    try {
      const v = localStorage.getItem("atlas.tenantKnowledge.activeTab");
      return v === "guidance" || v === "proposed" ? (v as Tab) : "knowledge";
    } catch { return "knowledge"; }
  });
  const [pendingCount, setPendingCount] = useState<number>(0);

  const loadCounts = useCallback(async () => {
    // Top-level Proposed badge = knowledge proposals + AI-behaviour proposals.
    // Flagged sessions are deliberately NOT counted here: the `review/count`
    // source disagrees with what the queue renders, and the whole flagged-
    // sessions surface is being folded into the ADR-0148 capture-engine refactor.
    try {
      const [kp, gp] = await Promise.all([
        apiFetch<{ count: number }>("/api/tenant/admin/tenant-knowledge-proposals/pending-count").catch(() => ({ count: 0 })),
        apiFetch<{ proposals: unknown[] }>("/api/tenant/admin/tenant-guidance-proposals").catch(() => ({ proposals: [] })),
      ]);
      setPendingCount(kp.count + gp.proposals.length);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { void loadCounts(); }, [loadCounts]);

  useEffect(() => {
    try { localStorage.setItem("atlas.tenantKnowledge.activeTab", activeTab); } catch { /* ignore */ }
  }, [activeTab]);

  // ADR-0148 §4.1: two groups on one row. Library = the accepted memory you
  // curate (Knowledge = what your data means; Guidance = how the AI behaves).
  // Review = what needs your attention (Proposed captures). One page, one row —
  // the grouping does the delineation.
  const tabButton = (t: Tab, label: string) => (
    <button
      key={t}
      className={`btn${activeTab === t ? " btnPrimary" : ""}`}
      style={{ fontSize: 13, padding: "6px 14px", borderRadius: "6px 6px 0 0" }}
      onClick={() => setActiveTab(t)}
    >
      {label}
    </button>
  );
  const groupLabel = (text: string) => (
    <span className="muted" style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.6, textTransform: "uppercase", alignSelf: "center", marginRight: 2 }}>
      {text}
    </span>
  );
  const tabBar = (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 10, borderBottom: "1px solid var(--border)", marginBottom: 12, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
        {groupLabel("Library")}
        {tabButton("knowledge", "Business Knowledge")}
        {tabButton("guidance", "AI Behavior")}
      </div>
      <div style={{ width: 1, alignSelf: "stretch", background: "var(--border)", margin: "6px 6px 0" }} />
      <div style={{ display: "flex", alignItems: "flex-end", gap: 4 }}>
        {groupLabel("Review")}
        {tabButton("proposed", pendingCount > 0 ? `Proposed (${pendingCount >= 99 ? "99+" : pendingCount})` : "Proposed")}
      </div>
    </div>
  );

  if (activeTab === "knowledge") return <KnowledgeTab tabBar={tabBar} />;
  return (
    <PageLayout title="Tenant Knowledge">
      {tabBar}
      {activeTab === "guidance" && <GuidanceTab />}
      {activeTab === "proposed" && <ProposedTabComposite onResolved={loadCounts} />}
    </PageLayout>
  );
}

// ─────────────────────────────────────────────
// Sub-tabs — a lighter, second-level segmented control (distinct from the top
// tab bar), used inside Proposed for per-library sub-navigation.
// ─────────────────────────────────────────────

function SubTabs<T extends string>({ tabs, active, onSelect }: {
  tabs: Array<[T, string, number?]>;
  active: T;
  onSelect: (t: T) => void;
}) {
  return (
    <div style={{ display: "inline-flex", gap: 2, padding: 3, background: "var(--surface-2, rgba(128,128,128,0.10))", borderRadius: 8, flexWrap: "wrap", justifySelf: "start" }}>
      {tabs.map(([t, label, count]) => (
        <button
          key={t}
          className={`btn${active === t ? " btnPrimary" : ""}`}
          style={{ fontSize: 12, padding: "4px 12px", borderRadius: 6, ...(active === t ? {} : { background: "transparent", borderColor: "transparent" }) }}
          onClick={() => onSelect(t)}
        >
          {label}
          {typeof count === "number" && count > 0 && (
            <span className="pill" style={{ marginLeft: 6, fontSize: 10 }}>{count >= 99 ? "99+" : count}</span>
          )}
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// Proposed tab (ADR-0148 §4.2) — captured learnings awaiting review, sub-tabbed
// by library so nothing gets buried: Business Knowledge · AI Behavior.
// (Flagged-session review dissolved into the capture engine + Learning
// telemetry per the one-engine correction — the retro sweep drafts typed
// proposals straight into these two queues; no separate flagged-sessions tab.)
// ─────────────────────────────────────────────

type ProposedSub = "knowledge" | "behavior";

function ProposedTabComposite({ onResolved }: { onResolved: () => void }) {
  const [sub, setSub] = useState<ProposedSub>("knowledge");
  const [counts, setCounts] = useState({ knowledge: 0, behavior: 0 });

  const loadCounts = useCallback(async () => {
    const [k, b] = await Promise.all([
      apiFetch<{ count: number }>("/api/tenant/admin/tenant-knowledge-proposals/pending-count").catch(() => ({ count: 0 })),
      apiFetch<{ proposals: unknown[] }>("/api/tenant/admin/tenant-guidance-proposals").catch(() => ({ proposals: [] })),
    ]);
    setCounts({ knowledge: k.count, behavior: b.proposals.length });
  }, []);
  useEffect(() => { void loadCounts(); }, [loadCounts]);

  const afterResolve = () => { onResolved(); void loadCounts(); };

  return (
    <div style={{ display: "grid", gap: 10 }}>
      <SubTabs
        active={sub}
        onSelect={setSub}
        tabs={[
          ["knowledge", "Business Knowledge", counts.knowledge],
          ["behavior", "AI Behavior", counts.behavior],
        ]}
      />
      {sub === "knowledge" && (
        <div style={{ display: "grid", gap: 6 }}>
          <div className="help">Captured facts about your data and business, awaiting review.</div>
          <TenantKnowledgeProposedTab onResolved={afterResolve} />
        </div>
      )}
      {sub === "behavior" && (
        <div style={{ display: "grid", gap: 6 }}>
          <div className="help">Captured AI-behaviour rules, awaiting review. Accepting one puts it into effect for your workspace.</div>
          <GuidanceProposalsSection onResolved={afterResolve} />
        </div>
      )}
    </div>
  );
}

type GuidanceProposal = {
  id: string;
  kind: string;
  name: string;
  agent_type: string;
  category: string;
  description: string;
  origin?: string | null;
  /** Machine identifiers — rendered through `proposedByLabel`, never shown raw. */
  proposed_by?: string | null;
  updated_by?: string;
  session_owner?: string | null;
  supersedes?: string[] | null;
  supersedes_pending?: Array<{ id: string; name: string | null; present: boolean }>;
  superseded_by?: Array<{ id: string; name: string }>;
  rationale?: string;
  created_at: string;
  body: string;
  /** ADR-0201 §3.4 — the verdict this proposal was written with; null if never checked. */
  check?: CurationCheck | null;
  check_stale?: boolean;
};

function GuidanceProposalsSection({ onResolved }: { onResolved: () => void }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [proposals, setProposals] = useState<GuidanceProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  useViewportFrame(gridRef, { active: !loading && !error });
  const [busy, setBusy] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ proposals: GuidanceProposal[] }>("/api/tenant/admin/tenant-guidance-proposals");
      setProposals(res.proposals);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  /** ADR-0201 §3.4 — re-run a proposal's check against canon as it is now. */
  async function recheck(id: string) {
    setBusy(id);
    try {
      const res = await apiFetch<{ proposal: GuidanceProposal }>(
        `/api/tenant/admin/tenant-guidance-proposals/${id}/recheck`,
        { method: "POST" }
      );
      setProposals((rows) => rows.map((r) => (r.id === id ? { ...r, check: res.proposal.check ?? null } : r)));
      toast.push({ kind: "success", message: "Checked again." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Check failed" });
    } finally {
      setBusy(null);
    }
  }

  async function resolve(id: string, action: "accept" | "reject") {
    if (action === "reject") {
      const ok = await confirm({ title: "Reject proposal?", detail: "Delete this guidance proposal.", confirmLabel: "Reject" });
      if (!ok) return;
    }
    setBusy(id);
    try {
      await apiFetch(`/api/tenant/admin/tenant-guidance-proposals/${id}/resolve`, { method: "POST", body: { action } });
      toast.push({ kind: "success", message: action === "accept" ? "Accepted." : "Rejected." });
      await load();
      onResolved();
    } catch (e) {
      // No eval_regression special-case: the tenant resolve path has no
      // synchronous eval gate (ADR-0167 — accept lands the entry live), so
      // that code never comes back; show the route's real error.
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
    } finally {
      setBusy(null);
    }
  }

  if (error) return <ErrorState detail={error} />;
  if (loading) return <LoadingState />;
  if (proposals.length === 0) return <div className="muted" style={{ fontSize: 13, padding: "8px 0" }}>No guidance proposals.</div>;

  return (
    <div className="tableWrap tableWrapFrame" ref={gridRef}>
      <table className="table">
        <thead><tr><th>Name</th><th>Agent</th><th>Description</th><th>Proposed by</th><th>Origin</th><th /></tr></thead>
        <tbody>
          {proposals.map((p) => (
            <Fragment key={p.id}>
              <tr style={{ cursor: "pointer" }} onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                <td className="mono" style={{ fontSize: 12 }}>
                  <button
                    className="adminListAction"
                    aria-expanded={expanded === p.id}
                    onClick={(ev) => { ev.stopPropagation(); setExpanded(expanded === p.id ? null : p.id); }}
                  >{expanded === p.id ? "▾" : "▸"}</button> {p.name}
                  {(p.superseded_by?.length ?? 0) > 0 ? (
                    <span className={supersessionPillClass("superseded")}> superseded</span>
                  ) : (p.supersedes_pending ?? []).some((x) => x.present) ? (
                    <span className={supersessionPillClass("supersedes")}> supersedes</span>
                  ) : null}
                </td>
                <td>{agentPill(p.agent_type)}</td>
                <td className="muted" style={{ fontSize: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={p.description}>{p.description}</td>
                <td className="muted" style={{ fontSize: 11 }}>{proposedByLabel(p.proposed_by, p.updated_by, p.session_owner)}</td>
                <td className="muted" style={{ fontSize: 11 }} title={p.origin || ""}>{proposalOriginLabel(p.origin, new Map())}</td>
                <td className="actions" onClick={(ev) => ev.stopPropagation()}>
                  <button className="adminListAction" disabled={busy === p.id} onClick={() => void resolve(p.id, "accept")}>Accept</button>
                  <button className="adminListAction isDanger" disabled={busy === p.id} onClick={() => void resolve(p.id, "reject")}>Reject</button>
                </td>
              </tr>
              {expanded === p.id && (
                <tr>
                  <td colSpan={6} style={{ background: "var(--surface-2, transparent)" }}>
                    {p.rationale && <div className="help" style={{ marginBottom: 6 }}>Rationale: {p.rationale}</div>}
                    {(p.supersedes_pending ?? []).some((x) => x.present) && (
                      <div className="help" style={{ marginBottom: 6 }}>
                        Replaces {(p.supersedes_pending ?? []).filter((x) => x.present).map((x) => x.name).join(", ")} —
                        accepting this rejects {(p.supersedes_pending ?? []).filter((x) => x.present).length === 1 ? "it" : "them"}.
                      </div>
                    )}
                    {(p.superseded_by?.length ?? 0) > 0 && (
                      <div className="help" style={{ marginBottom: 6 }}>
                        Replaced by {(p.superseded_by ?? []).map((x) => x.name).join(", ")} — resolve that one instead.
                      </div>
                    )}
                    {/* ADR-0201 §3.4 — the review opens with the verdict already in hand. */}
                    <CurationVerdictPanel
                      check={p.check ?? null}
                      surface="guidance"
                      stale={p.check_stale === true}
                      onRecheck={() => void recheck(p.id)}
                      recheckBusy={busy === p.id}
                    />
                    <MarkdownView body={p.body} />
                  </td>
                </tr>
              )}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function KnowledgeTab({ tabBar }: { tabBar: React.ReactNode }) {
  const toast = useToast();
  const navigate = useNavigate();
  const { confirm } = useConfirm();
  const fileRef = useRef<HTMLInputElement>(null);
  const [restoring, setRestoring] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [entries, setEntries] = useState<KnowledgeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  useViewportFrame(gridRef, { active: !loading && !error });
  const [typeFilter, setTypeFilter] = useState<string>(() => {
    try { return localStorage.getItem("atlas.tenantKnowledge.knowledgeType") ?? ""; } catch { return ""; }
  });
  const [statusFilter, setStatusFilter] = useState<string>(() => {
    try { return localStorage.getItem("atlas.tenantKnowledge.knowledgeStatus") ?? ""; } catch { return ""; }
  });
  const [lapsedFilter, setLapsedFilter] = useState<string>("");
  // ADR-0186 usage-column sort: null = server order. First click sorts desc
  // (most-touched first); second click flips asc (dead-first for timestamps).
  const [sortKey, setSortKey] = useState<KnowledgeSortKey | null>(null);
  const [sortDir, setSortDir] = useState<KnowledgeSortDir>("desc");
  const toggleSort = (col: KnowledgeSortKey) => {
    if (sortKey !== col) {
      setSortKey(col);
      setSortDir("desc");
    } else {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    }
  };
  const sortedEntries = useMemo(() => {
    if (!sortKey) return entries;
    const dir = sortDir === "asc" ? 1 : -1;
    const value = (e: KnowledgeEntry): number => {
      switch (sortKey) {
        case "updated": return usageTimeSortValue(e.updated_at);
        case "search_hits": return e.usage ? e.usage.served_in_search : -Infinity;
        case "reads": return e.usage ? e.usage.read_count : -Infinity;
        case "last_hit": return usageTimeSortValue(e.usage?.last_served_at);
        case "last_read": return usageTimeSortValue(e.usage?.last_read_at);
        // ADR-0201: ascending puts the oldest verification — and every lapsed
        // entry — at the top, which is the curator's queue.
        case "verified": return usageTimeSortValue(e.verified_at);
      }
    };
    return [...entries].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va !== vb) return (va < vb ? -1 : 1) * dir;
      return a.name.localeCompare(b.name);
    });
  }, [entries, sortKey, sortDir]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSelected(new Set());
    try {
      const params = new URLSearchParams();
      if (typeFilter) params.set("type", typeFilter);
      if (statusFilter) params.set("status", statusFilter);
      const qs = params.toString();
      const url = `/api/tenant/admin/tenant-knowledge${qs ? `?${qs}` : ""}`;
      const res = await apiFetch<{ tenant_knowledge: KnowledgeEntry[] }>(url);
      setEntries(res.tenant_knowledge);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [typeFilter, statusFilter]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    try { localStorage.setItem("atlas.tenantKnowledge.knowledgeType", typeFilter); } catch { /* ignore */ }
  }, [typeFilter]);
  useEffect(() => {
    try { localStorage.setItem("atlas.tenantKnowledge.knowledgeStatus", statusFilter); } catch { /* ignore */ }
  }, [statusFilter]);

  async function handleExport() {
    try {
      const res = await apiFetch<{ version: string; exported_at: string; knowledge: unknown[] }>("/api/tenant/admin/tenant-knowledge-export");
      const blob = new Blob([JSON.stringify(res, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant-knowledge-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.push({ kind: "success", message: "Exported." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Export failed" });
    }
  }

  async function handleImport(file: File) {
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      const res = await apiFetch<{ imported: { knowledge: number; skipped: Array<{ name: string; reason: string }> } }>(
        "/api/tenant/admin/tenant-knowledge-import",
        { method: "POST", body: bundle }
      );
      const skipped = res.imported.skipped.length;
      toast.push({
        kind: skipped > 0 ? "info" : "success",
        message: `Imported ${res.imported.knowledge} entries${skipped > 0 ? `, ${skipped} skipped` : ""}.`,
      });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Import failed" });
    }
  }

  async function restoreFromRow(name: string) {
    const ok = await confirm({
      title: "Restore entry?",
      detail: `Restore "${name}" to your live knowledge base? It rejoins the index and the query agent's context immediately.`,
      confirmLabel: "Restore",
    });
    if (!ok) return;
    setRestoring(name);
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge/${name}/restore`, { method: "POST" });
      toast.push({ kind: "success", message: "Entry restored." });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Restore failed" });
    } finally {
      setRestoring(null);
    }
  }

  function toggleOne(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleAll() {
    setSelected((prev) => (prev.size === entries.length ? new Set() : new Set(entries.map((e) => e.name))));
  }

  async function purgeSelected() {
    const names = [...selected];
    if (names.length === 0) return;
    const ok = await confirm({
      title: "Purge archived entries?",
      detail: `Permanently delete ${names.length} archived ${names.length === 1 ? "entry" : "entries"}. This cannot be undone from the console.`,
      confirmLabel: "Purge",
    });
    if (!ok) return;
    setBulkBusy(true);
    try {
      const res = await apiFetch<{ purgedCount: number }>("/api/tenant/admin/tenant-knowledge-purge-archived", {
        method: "POST",
        body: { names },
      });
      toast.push({ kind: "success", message: `Purged ${res.purgedCount} ${res.purgedCount === 1 ? "entry" : "entries"}.` });
      await load();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Purge failed" });
    } finally {
      setBulkBusy(false);
    }
  }

  if (error) return <PageLayout title="Tenant Knowledge">{tabBar}<ErrorState detail={error} /></PageLayout>;
  if (loading) return <PageLayout title="Tenant Knowledge">{tabBar}<LoadingState /></PageLayout>;

  const isArchivedView = statusFilter === "archived";
  // ADR-0201 §8.4 — lapsed is a FILTER, not a second value crowding the date cell.
  const visibleEntries = lapsedFilter === "lapsed" ? sortedEntries.filter((e) => e.lapsed) : sortedEntries;

  return (
    <PageLayout title="Tenant Knowledge">
      {tabBar}
      <div style={{ display: "grid", gap: 12 }}>
        <div className="toolbar">
          <div className="toolbarGroup">
            <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <span className="label" style={{ fontSize: 12 }}>Type:</span>
              <select className="select" style={{ fontSize: 12 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All</option>
                {KNOWLEDGE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
              </select>
            </label>
            <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
              <span className="label" style={{ fontSize: 12 }}>Status:</span>
              <select className="select" style={{ fontSize: 12 }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Live</option>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            {!isArchivedView && (
              <label className="field" style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
                <span className="label" style={{ fontSize: 12 }}>Verification:</span>
                <select
                  className="select"
                  style={{ fontSize: 12 }}
                  value={lapsedFilter}
                  title={VERIFY_HELP.lapsed}
                  onChange={(e) => setLapsedFilter(e.target.value)}
                >
                  <option value="">All</option>
                  <option value="lapsed">Lapsed only</option>
                </select>
              </label>
            )}
          </div>
          <div className="toolbarGroup">
            {isArchivedView && selected.size > 0 && (
              <button className="btn btnDanger" disabled={bulkBusy} onClick={() => void purgeSelected()}>
                {bulkBusy ? "Purging…" : `Purge selected (${selected.size})`}
              </button>
            )}
            <input type="file" ref={fileRef} accept=".json" style={{ display: "none" }} onChange={(e) => { if (e.target.files?.[0]) void handleImport(e.target.files[0]); e.target.value = ""; }} />
            <button className="btn" onClick={() => fileRef.current?.click()}>Import</button>
            <button className="btn" onClick={() => void handleExport()}>Export</button>
            <button className="btn btnPrimary" onClick={() => navigate("/app/admin/tenant-knowledge/new")}>+ New Entry</button>
          </div>
        </div>
        {!isArchivedView && (
          <div className="muted" style={{ fontSize: 12 }}>{USAGE_HELP.caption}</div>
        )}
        <div className="tableWrap tableWrapFrame" ref={gridRef}>
          <table className="table">
            <thead>
              {isArchivedView ? (
                <tr>
                  <th style={{ width: 28 }}>
                    <input
                      type="checkbox"
                      aria-label="Select all archived entries"
                      checked={entries.length > 0 && selected.size === entries.length}
                      ref={(el) => { if (el) el.indeterminate = selected.size > 0 && selected.size < entries.length; }}
                      onChange={() => toggleAll()}
                    />
                  </th>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Reason</th>
                  <th>Archived</th>
                  <th />
                </tr>
              ) : (
                <tr>
                  <th>Name</th>
                  <th>Description</th>
                  <th>Type</th>
                  <th>Status</th>
                  <KnowledgeSortableTH col="updated" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("updated")}>Updated</KnowledgeSortableTH>
                  <KnowledgeSortableTH col="search_hits" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("search_hits")} title={USAGE_HELP.searchHits}>Search hits</KnowledgeSortableTH>
                  <KnowledgeSortableTH col="reads" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("reads")} title={USAGE_HELP.reads}>Reads</KnowledgeSortableTH>
                  <KnowledgeSortableTH col="last_hit" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("last_hit")} title={USAGE_HELP.lastHit}>Last hit</KnowledgeSortableTH>
                  <KnowledgeSortableTH col="last_read" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("last_read")} title={USAGE_HELP.lastRead}>Last read</KnowledgeSortableTH>
                  <KnowledgeSortableTH col="verified" sortKey={sortKey} sortDir={sortDir} onClick={() => toggleSort("verified")} title={VERIFY_HELP.column}>Verified</KnowledgeSortableTH>
                </tr>
              )}
            </thead>
            <tbody>
              {entries.length === 0 && (
                <tr><td colSpan={isArchivedView ? 7 : 10} className="muted" style={{ textAlign: "center", padding: 24 }}>
                  {isArchivedView ? "No archived entries." : "No knowledge entries yet."}
                </td></tr>
              )}
              {isArchivedView
                ? entries.map((e) => (
                    <tr key={e.name} style={{ cursor: "pointer" }} onClick={() => navigate(`/app/admin/tenant-knowledge/entry/${e.name}`)}>
                      <td onClick={(ev) => ev.stopPropagation()}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${e.name}`}
                          checked={selected.has(e.name)}
                          onChange={() => toggleOne(e.name)}
                        />
                      </td>
                      <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.name}</td>
                      <td className="muted" style={{ fontSize: 12, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.description}>{e.description}</td>
                      <td>{typePill(e.type)}</td>
                      <td className="muted" style={{ fontSize: 12, maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.archive_reason || ""}>{e.archive_reason || "—"}</td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{e.archived_at ? fmtDateTime(e.archived_at) : "—"}</td>
                      <td onClick={(ev) => ev.stopPropagation()}>
                        <button className="adminListAction" disabled={restoring === e.name} onClick={() => void restoreFromRow(e.name)}>
                          {restoring === e.name ? "Restoring…" : "Restore"}
                        </button>
                      </td>
                    </tr>
                  ))
                : visibleEntries.map((e) => (
                    <tr key={e.name} style={{ cursor: "pointer" }} onClick={() => navigate(`/app/admin/tenant-knowledge/entry/${e.name}`)}>
                      <td className="mono" style={{ fontSize: 12, whiteSpace: "nowrap" }}>{e.name}</td>
                      <td className="muted" style={{ fontSize: 12, maxWidth: 420, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.description}>{e.description}</td>
                      <td>{typePill(e.type)}</td>
                      <td>{statusPill(e.status)}</td>
                      <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(e.updated_at)}</td>
                      {usageCountCell(e.usage, e.usage?.served_in_search)}
                      {usageCountCell(e.usage, e.usage?.read_count)}
                      {usageTimeCell(e.usage, e.usage?.last_served_at)}
                      {usageTimeCell(e.usage, e.usage?.last_read_at)}
                      {verifiedCell(e)}
                    </tr>
                  ))}
            </tbody>
          </table>
        </div>
      </div>
    </PageLayout>
  );
}

// ─────────────────────────────────────────────
// Entry editor — detail page (/app/admin/tenant-knowledge/entry/:name and /new)
// ─────────────────────────────────────────────

export function TenantKnowledgeEntryEditor() {
  const { name: routeName } = useParams<{ name: string }>();
  const isNew = routeName === undefined;
  // ADR-0214 C7: opened from the Activity trail, the trail sits above the page's own breadcrumb.
  const trail = useWalkedTrail();
  const walked = trail.length ? <TrailLinks trail={trail} /> : undefined;
  const toast = useToast();
  const { confirm } = useConfirm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [candidates, setCandidates] = useState<Array<{ name: string; type: string }>>([]);
  const [meta, setMeta] = useState<{ created_at: string; updated_at: string; updated_by: string; status: string; archive_reason?: string; archived_at?: string } | null>(null);
  const [usage, setUsage] = useState<EntryUsage | null>(null);
  // ADR-0201: a refused save renders in place — a toast cannot be acted on.
  const [checkBlock, setCheckBlock] = useState<CurationCheck | null>(null);
  const [overrideReason, setOverrideReason] = useState("");
  const [relatedOffer, setRelatedOffer] = useState<string[]>([]);
  const [verification, setVerification] = useState<{ usage: EntryUsage; check: CurationCheck; lapsed: boolean } | null>(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [entryVerification, setEntryVerification] = useState<VerificationFields | null>(null);

  const [fName, setFName] = useState("");
  const [fType, setFType] = useState("join_path");
  const [fDescription, setFDescription] = useState("");
  const [fBody, setFBody] = useState("");
  const [fTags, setFTags] = useState("");
  const [fStatus, setFStatus] = useState("accepted");
  const [fRelated, setFRelated] = useState<string[]>([]);
  const [fCadence, setFCadence] = useState("");
  const [cadenceMsg, setCadenceMsg] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const list = await apiFetch<{ tenant_knowledge: KnowledgeEntry[] }>("/api/tenant/admin/tenant-knowledge");
        setCandidates(list.tenant_knowledge.filter((e) => e.name !== routeName).map((e) => ({ name: e.name, type: e.type })));
      } catch { /* related picker degrades gracefully */ }
      if (isNew) return;
      try {
        const res = await apiFetch<{ tenant_knowledge: KnowledgeEntry }>(`/api/tenant/admin/tenant-knowledge/${routeName}`);
        const e = res.tenant_knowledge;
        setFName(e.name); setFType(e.type); setFDescription(e.description);
        setFBody(e.body); setFTags(e.scope_tags.join(", "));
        setFStatus(e.status === "archived" ? "accepted" : e.status);
        setFRelated(e.related);
        setMeta({ created_at: e.created_at, updated_at: e.updated_at, updated_by: e.updated_by, status: e.status, archive_reason: e.archive_reason, archived_at: e.archived_at });
        setUsage(e.usage ?? null);
        setEntryVerification({ verified_at: e.verified_at, verified_by_actor: e.verified_by_actor, lapsed: e.lapsed });
        setFCadence(e.verify_every_days ? String(e.verify_every_days) : "");
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [routeName, isNew]);

  const nameValid = SLUG_RE.test(fName.trim());

  async function save(override?: string) {
    setBusy(true);
    setCheckBlock(null);
    try {
      const cadence = cadencePayload(fCadence);
      const body = {
        type: fType,
        description: fDescription.trim(),
        body: fBody,
        scope_tags: fTags.split(",").map((t) => t.trim()).filter(Boolean),
        status: fStatus,
        related: fRelated,
        ...(cadence !== undefined ? { verify_every_days: cadence } : {}),
        ...(override ? { override_reason: override } : {}),
      };
      const res = isNew
        ? await apiFetch<{ check: CurationCheck | null }>("/api/tenant/admin/tenant-knowledge", {
            method: "POST",
            body: { ...body, name: fName.trim() },
          })
        : await apiFetch<{ check: CurationCheck | null }>(`/api/tenant/admin/tenant-knowledge/${routeName}`, {
            method: "PUT",
            body,
          });

      // ADR-0201 §8.2 — a `complements` finding is an offer, not an auto-add.
      // Staying on the page to make it is worth more than the navigation.
      const offer = (res.check?.findings ?? [])
        .filter((f) => f.relation === "complements" && !fRelated.includes(f.name))
        .map((f) => f.name);
      if (offer.length > 0) {
        setRelatedOffer(offer);
        toast.push({ kind: "success", message: isNew ? "Entry created." : "Entry updated." });
        return;
      }
      toast.push({ kind: "success", message: isNew ? "Entry created." : "Entry updated." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      if (e instanceof ApiRequestError && e.code === "curation_check") {
        const check = (e.data as { check?: CurationCheck } | undefined)?.check;
        if (check) {
          setCheckBlock(check);
          return;
        }
      }
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Save failed" });
    } finally {
      setBusy(false);
    }
  }

  /** Accept the related-entry offer: one click, one PUT, no modal. */
  async function addRelated(names: string[]) {
    const next = [...fRelated, ...names.filter((n) => !fRelated.includes(n))];
    setFRelated(next);
    setRelatedOffer([]);
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge/${routeName ?? fName.trim()}`, {
        method: "PUT",
        body: { related: next },
      });
      toast.push({ kind: "success", message: "Related entries added." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not add related entries" });
    }
  }

  /** ADR-0201 §3.5 — usage plus a fresh check against canon as it is now. */
  async function loadVerification() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ usage: EntryUsage; check: CurationCheck; lapsed: boolean }>(
        `/api/tenant/admin/tenant-knowledge/${routeName}/verification`
      );
      setVerification(res);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not load verification" });
    } finally {
      setVerifyBusy(false);
    }
  }

  /** "Still right" — restamp, change nothing else. */
  async function markVerified() {
    if (!routeName) return;
    setVerifyBusy(true);
    try {
      const res = await apiFetch<{ tenant_knowledge: KnowledgeEntry }>(
        `/api/tenant/admin/tenant-knowledge/${routeName}/verify`,
        { method: "POST" }
      );
      const e = res.tenant_knowledge;
      setEntryVerification({ verified_at: e.verified_at, verified_by_actor: e.verified_by_actor, lapsed: e.lapsed });
      toast.push({ kind: "success", message: "Verified." });
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Could not verify" });
    } finally {
      setVerifyBusy(false);
    }
  }

  async function archive() {
    if (!routeName) return;
    const ok = await confirm({
      title: "Archive entry?",
      detail: `Archive "${routeName}"? It leaves the index and the query agent's context, but stays readable in the archive.`,
      confirmLabel: "Archive",
    });
    if (!ok) return;
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge/${routeName}/archive`, {
        method: "POST",
        body: { reason: "archived from console" },
      });
      toast.push({ kind: "success", message: "Entry archived." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Archive failed" });
    }
  }

  async function restore() {
    if (!routeName) return;
    const ok = await confirm({
      title: "Restore entry?",
      detail: `Restore "${routeName}" to your live knowledge base? It rejoins the index and the query agent's context immediately.`,
      confirmLabel: "Restore",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge/${routeName}/restore`, { method: "POST" });
      toast.push({ kind: "success", message: "Entry restored." });
      navigate(`/app/admin/tenant-knowledge/entry/${routeName}`);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Restore failed" });
      setBusy(false);
    }
  }

  async function purge() {
    if (!routeName) return;
    const ok = await confirm({
      title: "Purge archived entry?",
      detail: `Permanently delete "${routeName}" from the archive. This cannot be undone from the console.`,
      confirmLabel: "Purge",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await apiFetch(`/api/tenant/admin/tenant-knowledge/${routeName}/purge`, { method: "POST" });
      toast.push({ kind: "success", message: "Entry purged." });
      navigate("/app/admin/tenant-knowledge");
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Purge failed" });
      setBusy(false);
    }
  }

  const title = (
    <>
      <Link to="/app/admin/tenant-knowledge">Tenant Knowledge</Link>
      {" / "}
      {isNew ? "New entry" : routeName}
    </>
  );

  if (error) return <PageLayout title={title} breadcrumb={walked}><ErrorState detail={error} /></PageLayout>;
  if (loading) return <PageLayout title={title} breadcrumb={walked}><LoadingState /></PageLayout>;

  const isArchived = meta?.status === "archived";

  return (
    <PageLayout
      title={title}
      breadcrumb={walked}
      actions={
        <>
          <button className="btn" disabled={busy} onClick={() => navigate("/app/admin/tenant-knowledge")}>Cancel</button>
          {isArchived ? (
            <>
              <button className="btn btnDanger" disabled={busy} onClick={() => void purge()}>Purge</button>
              <button className="btn btnPrimary" disabled={busy} onClick={() => void restore()}>
                {busy ? "Working…" : "Restore"}
              </button>
            </>
          ) : (
            <>
              {!isNew && (
                <button className="btn btnDanger" disabled={busy} onClick={() => void archive()}>Archive</button>
              )}
              <button
                className="btn btnPrimary"
                disabled={busy || !nameValid || !fDescription.trim()}
                onClick={() => void save()}
              >
                {busy ? "Saving…" : "Save"}
              </button>
            </>
          )}
        </>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        {isArchived && (
          <div className="help">
            This entry is archived{meta?.archived_at ? ` (${fmtDateTime(meta.archived_at)}${meta?.updated_by ? `, by ${meta.updated_by}` : ""})` : ""}
            {meta?.archive_reason ? ` — reason: ${meta.archive_reason}` : ""}. Restore it to make it live and editable, or purge it to delete it permanently.
          </div>
        )}
        {checkBlock && (
          <CurationCheckCard
            check={checkBlock}
            surface="knowledge"
            overrideReason={overrideReason}
            onOverrideReasonChange={setOverrideReason}
            onCancel={() => setCheckBlock(null)}
            onOverride={() => void save(overrideReason.trim())}
            busy={busy}
          />
        )}
        {relatedOffer.length > 0 && (
          <div className="card curationRelatedLine">
            <span>Related to {relatedOffer.join(", ")}.</span>
            <button className="btn btnSm" onClick={() => void addRelated(relatedOffer)}>Add</button>
            <button className="adminListAction" onClick={() => { setRelatedOffer([]); navigate("/app/admin/tenant-knowledge"); }}>
              Not now
            </button>
          </div>
        )}
        <div className="knowledgeEditorLayout">
          <div className="card" style={{ display: "grid", gap: 10, alignSelf: "start", alignContent: "start" }}>
            <label className="field">
              <span className="label">Name</span>
              <input
                className="input mono"
                maxLength={64}
                value={fName}
                onChange={(e) => setFName(e.target.value)}
                disabled={!isNew}
                placeholder="kebab-case-slug"
              />
              <div className="help">
                {isNew
                  ? "Lowercase letters, digits, hyphens (3–64). Permanent — to rename later, archive and recreate."
                  : "Names are permanent. To rename, archive this entry and recreate it."}
              </div>
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
              <label className="field">
                <span className="label">Type</span>
                <select className="select" value={fType} disabled={isArchived} onChange={(e) => setFType(e.target.value)}>
                  {KNOWLEDGE_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="label">Status</span>
                <select className="select" value={fStatus} disabled={isArchived} onChange={(e) => setFStatus(e.target.value)}>
                  <option value="accepted">accepted</option>
                  <option value="draft">draft</option>
                </select>
              </label>
            </div>
            <div className="help" style={{ marginTop: -4 }}>Accepted entries guide the query agent.</div>
            <label className="field">
              <span className="label">Verify every (days)</span>
              <input
                className="input"
                type="number"
                min={1}
                max={365}
                value={fCadence}
                disabled={isArchived}
                onChange={(e) => setFCadence(e.target.value)}
                onBlur={() => setCadenceMsg(cadenceError(fCadence))}
                placeholder="90"
              />
              <div className="help">{cadenceMsg ?? VERIFY_HELP.cadenceField}</div>
            </label>
            <label className="field">
              <span className="label">Description</span>
              <textarea
                className="input"
                rows={2}
                maxLength={200}
                style={{ resize: "vertical" }}
                value={fDescription}
                readOnly={isArchived}
                onChange={(e) => setFDescription(e.target.value)}
                placeholder="One line — this is the entry's hook in the knowledge index"
              />
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
            <label className="field">
              <span className="label">Tags</span>
              <input className="input" placeholder="comma-separated" value={fTags} readOnly={isArchived} onChange={(e) => setFTags(e.target.value)} />
            </label>
            <div className="field">
              <span className="label">Related entries</span>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "center" }}>
                {fRelated.map((rname) => (
                  <span key={rname} className="pill pillOk" style={{ fontSize: 11, cursor: isArchived ? "default" : "pointer", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }} title={isArchived ? undefined : "Remove"} onClick={() => { if (!isArchived) setFRelated(fRelated.filter((r) => r !== rname)); }}>
                    {rname}{isArchived ? "" : " ×"}
                  </span>
                ))}
                <select
                  className="select"
                  style={{ fontSize: 12 }}
                  value=""
                  disabled={isArchived}
                  onChange={(e) => {
                    if (e.target.value && !fRelated.includes(e.target.value)) {
                      setFRelated([...fRelated, e.target.value]);
                    }
                    e.target.value = "";
                  }}
                >
                  <option value="">+ add</option>
                  {candidates.filter((c) => !fRelated.includes(c.name)).map((c) => (
                    <option key={c.name} value={c.name}>{c.name} ({c.type.replace(/_/g, " ")})</option>
                  ))}
                </select>
              </div>
            </div>
            </div>
          </div>

          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <span className="label">Body</span>
              <span className="muted" style={{ fontSize: 12 }}>Markdown — SQL in ```sql fences. Live preview on the right.</span>
            </div>
            <div className="knowledgeEditorSplit">
              <textarea
                className="input"
                value={fBody}
                readOnly={isArchived}
                onChange={(e) => setFBody(e.target.value)}
                placeholder={"## Rule\n\nDescribe the rule…\n\n```sql\nSELECT ...\n```"}
              />
              <div className="knowledgeMarkdownPane">
                <MarkdownView body={fBody || "*Nothing to preview yet.*"} />
              </div>
            </div>
          </div>
        </div>

        {!isNew && !isArchived && (
          <div className="card" style={{ display: "grid", gap: 8 }}>
            <div style={{ fontWeight: 600 }}>Verification</div>
            <div className="help">{VERIFY_HELP.column}</div>
            <div style={{ fontSize: 13 }}>
              {entryVerification ? verifiedLine(entryVerification) : "Never verified"}
              {entryVerification?.lapsed ? <> &middot; <span className="pill pillWarn">lapsed</span></> : null}
            </div>
            {usage && <div className="muted" style={{ fontSize: 12 }}>{usageProse(usage)}</div>}
            {verification ? (
              <CurationVerdictPanel check={verification.check} surface="knowledge" />
            ) : (
              <div className="help">
                Check this entry against the knowledge base as it is now before confirming it.
              </div>
            )}
            <div className="actions">
              {!verification && (
                <button className="btn btnSm" disabled={verifyBusy} onClick={() => void loadVerification()}>
                  {verifyBusy ? "Checking…" : "Check against current knowledge"}
                </button>
              )}
              <button className="btn btnSm" disabled={verifyBusy} onClick={() => void markVerified()}>
                Still right
              </button>
              <button className="btn btnSm isDanger" disabled={busy} onClick={() => void archive()}>
                Archive
              </button>
            </div>
          </div>
        )}
        {meta && (
          <div className="muted" style={{ fontSize: 12 }}>
            Created {fmtDateTime(meta.created_at)} &middot; Updated {fmtDateTime(meta.updated_at)} by {meta.updated_by}
          </div>
        )}
        {(isNew || isArchived) && usage && (
          <div className="muted" style={{ fontSize: 12 }}>{usageProse(usage)}</div>
        )}
      </div>
    </PageLayout>
  );
}

// ─────────────────────────────────────────────
// Guidance tab (ADR-0148 §4.3) — how this workspace's AI behaves
// ─────────────────────────────────────────────

type GuidanceEntry = {
  name: string;
  agent_type: string;
  category: string;
  priority: number;
  status: string;
  enabled: boolean;
  description: string;
  updated_at: string;
  updated_by: string;
  promotion_status?: string;
  /** ADR-0201 — when a curator last confirmed this entry, and whether that lapsed. */
  verified_at?: string | null;
  verified_by_actor?: string | null;
  lapsed?: boolean;
};

type GlobalRow = { name: string; agent_type: string; description: string; series_id: string; ignored: boolean };

type GuidanceMeta = { agent_types: string[]; categories: string[]; scope_tags: { functional: string[]; capability: string[] } };

function agentPill(t: string) {
  return <span className="pill">{t.replace(/_/g, " ")}</span>;
}

function GuidanceTab() {
  const navigate = useNavigate();
  const toast = useToast();
  const [entries, setEntries] = useState<GuidanceEntry[]>([]);
  const [meta, setMeta] = useState<GuidanceMeta | null>(null);
  const [agentFilter, setAgentFilter] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showGlobals, setShowGlobals] = useState(false);
  const [globals, setGlobals] = useState<GlobalRow[]>([]);
  const [globalsLoading, setGlobalsLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const globalsRef = useRef<HTMLDivElement>(null);
  // With platform patterns open, the two grids split the viewport.
  useViewportFrame(gridRef, { active: !loading, fraction: showGlobals ? 0.5 : 1 });
  useViewportFrame(globalsRef, { active: showGlobals && !globalsLoading, minHeight: 160 });

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (agentFilter) params.set("agent_type", agentFilter);
      if (q.trim()) params.set("q", q.trim());
      const [m, list] = await Promise.all([
        meta ? Promise.resolve(meta) : apiFetch<GuidanceMeta>("/api/tenant/admin/tenant-guidance-meta"),
        apiFetch<{ entries: GuidanceEntry[] }>(`/api/tenant/admin/tenant-guidance${params.toString() ? `?${params}` : ""}`),
      ]);
      setMeta(m);
      setEntries(list.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentFilter, q]);

  useEffect(() => { void load(); }, [load]);

  const loadGlobals = useCallback(async () => {
    setGlobalsLoading(true);
    try {
      const res = await apiFetch<{ globals: GlobalRow[] }>("/api/tenant/admin/tenant-guidance-globals");
      setGlobals(res.globals);
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed to load platform patterns" });
    } finally {
      setGlobalsLoading(false);
    }
  }, [toast]);

  useEffect(() => { if (showGlobals && globals.length === 0) void loadGlobals(); }, [showGlobals, globals.length, loadGlobals]);

  async function toggleIgnore(row: GlobalRow) {
    setBusy(row.series_id);
    try {
      if (row.ignored) {
        await apiFetch(`/api/tenant/admin/tenant-guidance-suppressions/${row.series_id}`, { method: "DELETE" });
      } else {
        await apiFetch("/api/tenant/admin/tenant-guidance-suppressions", {
          method: "POST",
          body: { series_id: row.series_id, suppressed_name: row.name, agent_type: row.agent_type },
        });
      }
      await loadGlobals();
    } catch (e) {
      toast.push({ kind: "error", message: e instanceof Error ? e.message : "Failed" });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{ display: "grid", gap: 12 }}>
      <div className="help">How Atlas's AI behaves for your workspace — the rules that shape its agents. (Knowledge is how your data works; Guidance is how the AI behaves.)</div>
      <div className="toolbar">
        <div className="toolbarGroup">
          <select className="select" value={agentFilter} onChange={(e) => setAgentFilter(e.target.value)}>
            <option value="">All agents</option>
            {(meta?.agent_types ?? []).map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
          </select>
          <input className="input" placeholder="Search guidance…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="toolbarGroup" style={{ marginLeft: "auto" }}>
          <button className="btn" onClick={() => setShowGlobals((v) => !v)}>{showGlobals ? "Hide platform patterns" : "Platform patterns"}</button>
          <button className="btn btnPrimary" onClick={() => navigate("/app/admin/tenant-guidance/new")}>+ New entry</button>
        </div>
      </div>

      {error && <ErrorState detail={error} />}
      {loading ? <LoadingState /> : (
        <div className="tableWrap tableWrapFrame" ref={gridRef}>
          <table className="table">
            <thead><tr><th>Name</th><th>Description</th><th>Agent</th><th>Priority</th><th>Status</th><th>Updated</th><th title={VERIFY_HELP.column}>Verified</th></tr></thead>
            <tbody>
              {entries.length === 0 && <tr><td colSpan={7} className="muted" style={{ textAlign: "center", padding: 24 }}>No guidance entries yet.</td></tr>}
              {entries.map((e) => (
                <tr key={e.name} style={{ cursor: "pointer" }} onClick={() => navigate(`/app/admin/tenant-guidance/entry/${e.name}`)}>
                  <td className="mono" style={{ fontSize: 12 }}>{e.name}</td>
                  <td className="muted" style={{ fontSize: 12, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={e.description}>{e.description}</td>
                  <td>{agentPill(e.agent_type)}</td>
                  <td>{e.priority}</td>
                  <td>
                    {!e.enabled ? <span className="pill pillBad">disabled</span> : <span className={`pill ${e.status === "accepted" ? "pillOk" : "pillWarn"}`}>{e.status}</span>}
                    {e.promotion_status === "pending" && <span className="pill pillWarn" style={{ marginLeft: 4 }} title="Awaiting platform review">↑</span>}
                    {e.promotion_status === "accepted" && <span className="pill pillOk" style={{ marginLeft: 4 }} title="Accepted platform-wide">↑</span>}
                  </td>
                  <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{fmtDateTime(e.updated_at)}</td>
                  {verifiedCell(e)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showGlobals && (
        <div className="card" style={{ display: "grid", gap: 8 }}>
          <div style={{ fontWeight: 600 }}>Platform patterns active for this workspace</div>
          <div className="help">Guidance the platform applies to your agents. Ignore one to exclude it for this workspace only.</div>
          {globalsLoading ? <LoadingState /> : (
            <div className="tableWrap tableWrapFrame" ref={globalsRef}>
              <table className="table">
                <thead><tr><th>Name</th><th>Description</th><th>Agent</th><th /></tr></thead>
                <tbody>
                  {globals.length === 0 && <tr><td colSpan={4} className="muted" style={{ textAlign: "center", padding: 16 }}>No platform patterns.</td></tr>}
                  {globals.map((g) => (
                    <tr key={g.series_id}>
                      <td className="mono" style={{ fontSize: 12 }}>{g.name}{g.ignored && <span className="pill pillBad" style={{ marginLeft: 6, fontSize: 10 }}>ignored</span>}</td>
                      <td className="muted" style={{ fontSize: 12, maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={g.description}>{g.description}</td>
                      <td>{agentPill(g.agent_type)}</td>
                      <td className="actions">
                        <button className={`adminListAction${g.ignored ? "" : " isDanger"}`} disabled={busy === g.series_id} onClick={() => void toggleIgnore(g)}>
                          {g.ignored ? "Unignore" : "Ignore for this workspace"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

